/**
 * In Store Events Page - Admin Event Management
 * 
 * Allows Tammy to create and manage in-store events like:
 * - Decorating Classes
 * - Birthday Parties
 * - And more...
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiEye,
  HiEyeOff,
  HiRefresh,
  HiColorSwatch,
  HiExternalLink,
  HiGlobe,
  HiHome,
  HiStar,
} from 'react-icons/hi';
import Tooltip from '../../components/Tooltip';
import { adminFetch, adminHeaders } from '../lib/adminApi';
import ImageSelector from '../components/ImageSelector';

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  color: string;
  is_active: boolean;
  has_landing_page: boolean;
  display_order: number;
  // Homepage banner fields
  show_on_homepage: boolean;
  homepage_priority: number;
  homepage_tagline: string | null;
  created_at: string;
  updated_at: string;
}

const emptyForm: Omit<EventCategory, 'id' | 'created_at' | 'updated_at'> = {
  name: '',
  slug: '',
  description: '',
  image_url: '',
  color: '#8B5CF6',
  is_active: true,
  has_landing_page: false,
  display_order: 0,
  show_on_homepage: false,
  homepage_priority: 0,
  homepage_tagline: '',
};

export default function EventCategories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch('/api/event-categories');
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Auto-generate slug when name changes
  useEffect(() => {
    if (form.name && !editingId) {
      setForm(prev => ({ ...prev, slug: generateSlug(prev.name) }));
    }
  }, [form.name, editingId]);

  // Open modal for new category
  const openNewModal = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (category: EventCategory) => {
    setForm({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image_url: category.image_url || '',
      color: category.color,
      is_active: category.is_active,
      has_landing_page: category.has_landing_page,
      display_order: category.display_order,
      show_on_homepage: category.show_on_homepage || false,
      homepage_priority: category.homepage_priority || 0,
      homepage_tagline: category.homepage_tagline || '',
    });
    setEditingId(category.id);
    setShowModal(true);
  };

  // Save category
  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const url = editingId ? `/api/event-categories/${editingId}` : '/api/event-categories';
      const method = editingId ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...form,
          slug: form.slug || generateSlug(form.name),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save category');
      }

      setShowModal(false);
      fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const toggleActive = async (id: string) => {
    const category = categories.find(c => c.id === id);
    if (!category) return;

    try {
      const response = await adminFetch(`/api/event-categories/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ is_active: !category.is_active }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update event';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Delete category
  const deleteCategory = async (id: string) => {
    if (!confirm('Delete this event? This cannot be undone.')) return;

    try {
      const response = await adminFetch(`/api/event-categories/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete');
      }
      fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete event';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Toggle landing page
  const toggleLandingPage = async (id: string, enable: boolean) => {
    try {
      const response = await adminFetch(`/api/event-categories/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ has_landing_page: enable }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      
      if (enable) {
        // Navigate to editor after enabling
        navigate(`/admin/event-categories/${id}/editor`);
      } else {
        fetchCategories();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update landing page setting';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Toggle homepage banner visibility
  const toggleHomepage = async (id: string) => {
    const category = categories.find(c => c.id === id);
    if (!category) return;

    try {
      const response = await adminFetch(`/api/event-categories/${id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ show_on_homepage: !category.show_on_homepage }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update');
      }
      fetchCategories();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update homepage visibility';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">In Store Events</h1>
          <p className="text-gray-500 mt-1">
            Organize your events and services (Decorating Classes, Birthday Parties, etc.)
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchCategories}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Refresh"
            aria-label="Refresh categories"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openNewModal}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <HiPlus className="w-5 h-5" />
            New Event
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading categories...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && categories.length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <HiColorSwatch className="w-16 h-16 text-purple-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">No Categories Yet</h3>
          <p className="text-gray-500 mt-2 mb-4">
            Create your first in-store event to promote your services!
          </p>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <HiPlus className="w-5 h-5" />
            Create First Category
          </button>
        </div>
      )}

      {/* Categories Grid */}
      {!loading && categories.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Color indicator - using CSS custom property for dynamic user-defined colors */}
              {/* CSS variable set inline (required for dynamic values), but used via CSS class */}
              <div
                className="w-full h-2 rounded-t-lg mb-4 category-color-indicator"
                style={{ '--category-color': category.color } as React.CSSProperties}
                aria-label={`Color: ${category.color}`}
              />

              {/* Name and Status */}
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    category.is_active
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {category.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Description */}
              {category.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{category.description}</p>
              )}

              {/* Slug */}
              <p className="text-xs text-gray-400 font-mono mb-2">/{category.slug}</p>

              {/* Status Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {/* Landing Page Status */}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    category.has_landing_page
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <HiGlobe className="w-3 h-3" />
                  {category.has_landing_page ? 'Landing Page' : 'No Page'}
                </span>

                {/* Homepage Banner Status */}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    category.show_on_homepage
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <HiHome className="w-3 h-3" />
                  {category.show_on_homepage ? 'On Homepage' : 'Not on Homepage'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Homepage toggle - prominent button */}
                <Tooltip 
                  content={category.show_on_homepage 
                    ? 'Click to remove from homepage carousel' 
                    : 'Click to feature in homepage carousel banner'
                  } 
                  position="bottom-left"
                >
                  <button
                    onClick={() => toggleHomepage(category.id)}
                    className={`p-2 rounded-lg transition-colors ${
                      category.show_on_homepage
                        ? 'bg-pink-100 hover:bg-pink-200 text-pink-600'
                        : 'hover:bg-gray-100 text-gray-400'
                    }`}
                    title={category.show_on_homepage ? 'Remove from homepage carousel' : 'Feature on homepage carousel'}
                    aria-label={category.show_on_homepage ? 'Remove from homepage carousel' : 'Feature on homepage carousel'}
                  >
                    <HiStar className="w-5 h-5" />
                  </button>
                </Tooltip>

                <Tooltip content={category.is_active ? 'Hide event' : 'Show event'} position="bottom">
                  <button
                    onClick={() => toggleActive(category.id)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title={category.is_active ? 'Hide event' : 'Show event'}
                    aria-label={category.is_active ? 'Hide event' : 'Show event'}
                  >
                    {category.is_active ? (
                      <HiEyeOff className="w-5 h-5 text-gray-600" />
                    ) : (
                      <HiEye className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </Tooltip>
                <Tooltip content="Edit event">
                  <button
                    onClick={() => openEditModal(category)}
                    className="p-2 rounded-lg hover:bg-purple-50 transition-colors"
                    title="Edit event"
                    aria-label="Edit event"
                  >
                    <HiPencil className="w-5 h-5 text-purple-600" />
                  </button>
                </Tooltip>
                <Tooltip content="Delete event">
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                    title="Delete event"
                    aria-label="Delete event"
                  >
                    <HiTrash className="w-5 h-5 text-red-600" />
                  </button>
                </Tooltip>
                {category.has_landing_page && (
                  <>
                    <Tooltip content="Edit landing page">
                      <button
                        onClick={() => navigate(`/admin/event-categories/${category.id}/editor`)}
                        className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                        title="Edit landing page"
                        aria-label="Edit landing page"
                      >
                        <HiPencil className="w-5 h-5 text-blue-600" />
                      </button>
                    </Tooltip>
                    <Tooltip content="View landing page">
                      <a
                        href={`/events/${category.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-green-50 transition-colors"
                        title="View landing page"
                        aria-label="View landing page"
                      >
                        <HiExternalLink className="w-5 h-5 text-green-600" />
                      </a>
                    </Tooltip>
                  </>
                )}
                {!category.has_landing_page && (
                  <Tooltip content="Enable landing page">
                    <button
                      onClick={() => toggleLandingPage(category.id, true)}
                      className="px-3 py-1 text-sm border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      title="Enable landing page"
                      aria-label="Enable landing page"
                    >
                      <HiGlobe className="w-4 h-4 inline mr-1" />
                      Enable Landing Page
                    </button>
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingId ? 'Edit Event' : 'New Event'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Close"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Decorating Classes"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (URL-friendly)
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="e.g., decorating-classes"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated from name if left empty</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Describe this event..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Event Image */}
              <ImageSelector
                label="Event Image"
                value={form.image_url || ''}
                onChange={(url) => setForm({ ...form, image_url: url })}
                helpText="Image shown on event cards and landing pages"
              />

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-16 h-10 border rounded-lg cursor-pointer"
                    title="Select color"
                    aria-label="Select event color"
                  />
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                    placeholder="#8B5CF6"
                    title="Color hex code"
                    aria-label="Color hex code"
                  />
                </div>
              </div>

              {/* Display Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  aria-label="Display order"
                  title="Display order (lower numbers appear first)"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active (visible on website)
                </label>
              </div>

              {/* Landing Page Toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="has_landing_page"
                  checked={form.has_landing_page}
                  onChange={(e) => setForm({ ...form, has_landing_page: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="has_landing_page" className="text-sm font-medium text-gray-700">
                  Enable Landing Page (creates /events/:slug page)
                </label>
              </div>

              {/* Homepage Banner Section */}
              <div className="border-t pt-4 mt-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <HiHome className="w-4 h-4 text-pink-500" />
                  Homepage Banner
                </h3>

                {/* Show on Homepage Toggle */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="show_on_homepage"
                    checked={form.show_on_homepage}
                    onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })}
                    className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                  />
                  <label htmlFor="show_on_homepage" className="text-sm font-medium text-gray-700">
                    Feature on homepage banner
                  </label>
                </div>

                {form.show_on_homepage && (
                  <>
                    {/* Banner Tagline */}
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Banner Tagline
                      </label>
                      <input
                        type="text"
                        value={form.homepage_tagline || ''}
                        onChange={(e) => setForm({ ...form, homepage_tagline: e.target.value })}
                        placeholder="e.g., Valentine's Special! ✨"
                        maxLength={100}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Short catchy text shown on the banner</p>
                    </div>

                    {/* Banner Priority */}
                    <div>
                      <label htmlFor="homepage_priority" className="block text-sm font-medium text-gray-700 mb-1">
                        Banner Priority
                      </label>
                      <input
                        id="homepage_priority"
                        type="number"
                        value={form.homepage_priority}
                        onChange={(e) => setForm({ ...form, homepage_priority: parseInt(e.target.value) || 0 })}
                        min={0}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
                        title="Banner priority (lower numbers appear first)"
                        aria-label="Banner priority"
                      />
                      <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in the carousel</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-4 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
